import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {
  getLocalizedGiftName,
  getLocalizedAwardType,
} from '../../utils/multilingual';

import {
  ContentWrapper,
  Card,
  Button,
  Switch,
  Input,
  Dropdown,
} from '@components';
import {useMutation} from 'react-query';
import Bronze from '@images/bronze.svg';
import Silver from '@images/silver.svg';
import Gold from '@images/gold.svg';
import collapsedImage from '../../assets/images/collapsed-item.png';
import expandedImage from '../../assets/images/extended-item.png';
import {
  awardManage,
  getbonuses,
  delRewardApi,
  AddBonusesApi,
  awardSave,
  getProducts,
  PATCHUpdateRewardApi,
  doFetch,
} from '@apis';
import Modal from 'react-modal';
import toast from 'react-hot-toast';

const PointsInput = ({value, onChange, placeholder, ...rest}) => {
  return (
    <div className="relative">
      <input
        {...rest}
        value={value ?? ''}
        onChange={onChange}
        type="number"
        style={{maxWidth: 100, textAlign: 'center'}}
        className="rounded h-8 px-2 text-sm border bg-trueGray-100 bold-text"
      />
      <img
        src={Silver}
        alt="Silver Icon"
        className="h-4 absolute top-2 right-6"
      />
    </div>
  );
};

const TruncatedGiftName = ({giftName, noGiftText}) => {
  const displayName = giftName || noGiftText;

  return (
    <div
      className="truncate max-w-full"
      style={{
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
      title={displayName}
    >
      <h4>{displayName}</h4>
    </div>
  );
};

const PointsSetup = () => {
  const {t, i18n} = useTranslation();
  const pointexpire = [
    {label: t('pointsSetup.never'), value: t('pointsSetup.never')},
  ];

  const [state, setState] = useState({
    eligibility_points1: 0,
    eligibility_points2: 0,
    eligibility_points3: 0,
    discount1: 0,
    discount2: 0,
    discount3: 0,
    gift1: null,
    gift2: null,
    gift3: null,
    giftDisplay1: null,
    giftDisplay2: null,
    giftDisplay3: null,
    bonusData: [],
    productsData: [],
  });
  const [isEditedView, setEditedView] = useState(false);
  const [awardData, setawardData] = useState([]);
  const [bonusData, setbonusData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalIsOpen, setIsOpen] = useState(false);

  const [awardType, setAwardType] = useState('');
  const [awardTypeEs, setAwardTypeEs] = useState('');
  const [selectedLevelOption, setSelectedLevelOption] = useState('-1');
  const [enabled, setEnabled] = useState(true);
  const [discount, setDiscount] = useState(0);
  const [points, setPoints] = useState(0);
  const [levelOptionsDictLoading, setLevelOptionDictLoading] = useState(true);
  const [levelOptionsDict, setLevelOptionsDict] = useState([
    {label: t('pointsSetup.selectLevel'), value: '-1'},
  ]);
  const [refresh, setRefresh] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  // Validation functions
  const validateDiscount = value => {
    if (value === '' || value === null || value === undefined) {
      return t('pointsSetup.discountRequired', 'Discount is required');
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return t(
        'pointsSetup.discountValidNumber',
        'Discount must be a valid number',
      );
    }
    if (numValue < 0) {
      return t('pointsSetup.discountNegative', 'Discount cannot be negative');
    }
    if (numValue > 100) {
      return t('pointsSetup.discountExceedsMax', 'Discount cannot exceed 100%');
    }
    return '';
  };

  const validatePoints = value => {
    if (value === '' || value === null || value === undefined) {
      return t('pointsSetup.pointsRequired', 'Points is required');
    }
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      return t(
        'pointsSetup.pointsValidNumber',
        'Points must be a valid number',
      );
    }
    if (numValue < 0) {
      return t('pointsSetup.pointsNegative', 'Points cannot be negative');
    }
    if (!Number.isInteger(numValue)) {
      return t(
        'pointsSetup.pointsWholeNumber',
        'Points must be a whole number',
      );
    }
    return '';
  };

  // Validation to ensure at least one of discount or points has a value > 0
  const validateDiscountAndPoints = (discountValue, pointsValue) => {
    const discountNum = parseFloat(discountValue) || 0;
    const pointsNum = parseInt(pointsValue) || 0;

    if (discountNum === 0 && pointsNum === 0) {
      return t(
        'pointsSetup.discountOrPointsRequired',
        'Either discount or points must be greater than 0',
      );
    }
    return '';
  };

  // Controls the modal for creating a new reward
  const openModal = () => {
    // Reset form fields when opening the modal
    setAwardType('');
    setAwardTypeEs('');
    setDiscount(0);
    setPoints(0);
    // Set to placeholder value - user must select a valid level
    setSelectedLevelOption('-1');
    setEnabled(true);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const TableView = (val, key) => {
    // If clicking on the already selected row, collapse it
    if (selectedRow === key) {
      setSelectedRow(null);
    } else {
      setSelectedRow(key);
    }
  };

  // Fix for the checkbox handling
  const handleCheckboxChange = e => {
    setEnabled(e.target.checked);
  };

  // Enhanced input handlers to prevent negative values and empty values
  const handleDiscountChange = e => {
    const value = e.target.value;
    // Don't allow empty values - require a valid number
    if (value === '') {
      return; // Don't update state, keep previous value
    }
    // Convert to number and check if it's valid and non-negative
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setDiscount(value);
    }
    // If invalid, don't update the state (prevents the input from changing)
  };

  const handlePointsChange = e => {
    const value = e.target.value;
    // Don't allow empty values - require a valid number
    if (value === '') {
      return; // Don't update state, keep previous value
    }
    // Convert to number and check if it's valid, non-negative, and integer
    const numValue = parseInt(value);
    if (
      !isNaN(numValue) &&
      numValue >= 0 &&
      Number.isInteger(parseFloat(value))
    ) {
      setPoints(value);
    }
    // If invalid, don't update the state (prevents the input from changing)
  };

  // Prevent typing invalid characters
  const handleKeyDown = e => {
    // Prevent minus sign, plus sign, and 'e' (scientific notation)
    if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  // Improved error handling helper
  const handleApiError = (error, defaultMessage) => {
    console.error('API Error:', error);

    try {
      if (error && error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            // Sometimes the entire response is a string error message
            if (
              error.response.data.includes('IntegrityError') ||
              error.response.data.includes('unique constraint') ||
              error.response.data.includes('already exists')
            ) {
              // Duplicate entry error
              toast.error(
                t(
                  'pointsSetup.duplicateAward',
                  'An award with this name already exists for this level',
                ),
              );
            } else {
              toast.error(
                error.response.data ||
                  t('pointsSetup.serverResponseError', defaultMessage),
              );
            }
          } else if (error.response.data.detail) {
            // Our backend normally returns a 'detail' field with error info
            const detail = error.response.data.detail;
            if (
              typeof detail === 'string' &&
              (detail.includes('unique constraint') ||
                detail.includes('already exists'))
            ) {
              // Duplicate entry error
              toast.error(
                t(
                  'pointsSetup.duplicateAward',
                  'An award with this name already exists for this level',
                ),
              );
            } else {
              toast.error(
                detail || t('pointsSetup.serverResponseError', defaultMessage),
              );
            }
          } else if (error.response.data.award_type_en) {
            // Handle field-specific errors
            if (
              Array.isArray(error.response.data.award_type_en) &&
              error.response.data.award_type_en.length > 0 &&
              (error.response.data.award_type_en[0].includes(
                'unique constraint',
              ) ||
                error.response.data.award_type_en[0].includes('already exists'))
            ) {
              // Duplicate award_type_en field error
              toast.error(
                t(
                  'pointsSetup.duplicateAward',
                  'An award with this name already exists for this level',
                ),
              );
            } else {
              toast.error(
                error.response.data.award_type_en[0] ||
                  t('pointsSetup.serverResponseError', defaultMessage),
              );
            }
          } else {
            // Handle generic error response
            toast.error(t('pointsSetup.errorCreatingAward', defaultMessage));
          }
        } else if (error.response.status === 401) {
          toast.error(t('pointsSetup.unauthorized', 'Authentication required'));
        } else if (error.response.status === 403) {
          toast.error(
            t(
              'pointsSetup.forbidden',
              'You do not have permission to perform this action',
            ),
          );
        } else if (error.response.status === 500) {
          // Server error that might be caused by IntegrityError
          toast.error(
            t(
              'pointsSetup.serverError',
              'Server error. This could be due to a duplicate award name.',
            ),
          );
        } else {
          toast.error(t('pointsSetup.errorCreatingAward', defaultMessage));
        }
      } else if (error && error.request) {
        toast.error(
          t(
            'pointsSetup.networkError',
            'Network error, please check your connection',
          ),
        );
      } else {
        // Something happened in setting up the request that triggered an Error
        toast.error(t('pointsSetup.errorCreatingAward', defaultMessage));
      }
    } catch (handlingError) {
      // If anything goes wrong in our error handling, still show a message
      console.error('Error while handling API error:', handlingError);
      toast.error(t('pointsSetup.errorCreatingAward', defaultMessage));
    }
  };

  // Update the addAward function to use the improved error handling
  const addAward = async e => {
    e.preventDefault();

    // Validate inputs before submission (both English and Spanish)
    if (
      !awardType ||
      awardType.trim() === '' ||
      !awardTypeEs ||
      awardTypeEs.trim() === ''
    ) {
      toast.error(
        t(
          'pointsSetup.awardTypeRequired',
          'Both English and Spanish fields are required',
        ),
      );
      return;
    }

    const trimmedAwardType = awardType.trim();
    const trimmedAwardTypeEs = awardTypeEs.trim();

    // Extra validation to ensure non-empty award_type
    if (trimmedAwardType.length === 0 || trimmedAwardTypeEs.length === 0) {
      toast.error(
        t(
          'pointsSetup.awardTypeEmpty',
          'Both English and Spanish fields are required',
        ),
      );
      return;
    }

    if (selectedLevelOption === '-1' || !selectedLevelOption) {
      toast.error(
        t('pointsSetup.selectValidLevel', 'Please select a valid level'),
      );
      return;
    }

    // Validate discount and points
    const discountValidationError = validateDiscount(discount);
    const pointsValidationError = validatePoints(points);
    const discountAndPointsValidationError = validateDiscountAndPoints(
      discount,
      points,
    );

    if (discountValidationError) {
      toast.error(discountValidationError);
      return;
    }

    if (pointsValidationError) {
      toast.error(pointsValidationError);
      return;
    }

    if (discountAndPointsValidationError) {
      toast.error(discountAndPointsValidationError);
      return;
    }

    // Check if this level already has an award with this name
    const selectedLevel = awardData.find(
      level =>
        level &&
        level.id &&
        level.id.toString() === selectedLevelOption.toString(),
    );

    if (
      selectedLevel &&
      selectedLevel.awards &&
      Array.isArray(selectedLevel.awards)
    ) {
      // More thorough duplicate check - case insensitive and normalize whitespace
      const normalizedNewAwardType = trimmedAwardType
        .toLowerCase()
        .replace(/\s+/g, ' ');

      const existingAward = selectedLevel.awards.find(award => {
        if (!award || (!award.award_type_en && !award.award_type_es_mx))
          return false;
        // Normalize existing award types the same way for comparison
        const existingType =
          award.award_type_en || award.award_type_es_mx || '';
        const normalizedExistingType = existingType
          .toLowerCase()
          .replace(/\s+/g, ' ');
        return normalizedExistingType === normalizedNewAwardType;
      });

      if (existingAward) {
        // Use the same translation key as in the error handler
        toast.error(
          t(
            'pointsSetup.duplicateAward',
            'An award with this name already exists for this level',
          ),
        );
        return;
      }
    }

    // Update the data object with current values
    const newAwardData = {
      award_type_en: trimmedAwardType, // Use award_type_en as primary field
      award_type_es_mx: trimmedAwardTypeEs,
      enabled: enabled,
      discount: discount || 0,
      points: points || 0,
      level: selectedLevelOption,
    };

    // Check for empty award_type_en again
    if (!newAwardData.award_type_en) {
      toast.error(
        t('pointsSetup.awardTypeEmpty', 'Award type cannot be empty'),
      );
      return;
    }

    try {
      await doFetch('/admin-api/v1/level-awards/', {
        method: 'POST',
        body: JSON.stringify(newAwardData),
      });

      toast.success(t('pointsSetup.newAwardCreated', 'New award created'));
      setIsOpen(false);
      // Clear form fields after successful submission
      setAwardType('');
      setAwardTypeEs('');
      setDiscount(0);
      setPoints(0);
      setSelectedLevelOption('-1');
      setEnabled(true);
      // Refresh data after successful submission
      fetchData();
      // Force a refresh to update the UI
      setRefresh(prev => !prev);
    } catch (error) {
      handleApiError(
        error,
        t('pointsSetup.errorCreatingAward', 'Error creating award'),
      );
    }
  };

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: '30%',
    },
  };

  const addMore = async () => {
    var bonuses = bonusData;
    const newBonus = await AddBonusesApi({
      amount: 10,
      enabled: false,
      discount: null,
      points: null,
      gift: null,
    });
    const getbonusesresult = await getbonuses();
    setbonusData(getbonusesresult);
    setState(s => ({
      ...s,
      bonusData: getbonusesresult,
    }));
  };

  const fetchData = async () => {
    try {
      const [
        awardManageresult,
        getbonusesresult,
        getproductsresult,
      ] = await Promise.all([awardManage(), getbonuses(), getProducts()]);

      // Save the raw data
      setawardData(awardManageresult);
      setbonusData(getbonusesresult);

      // Update the state with processed data
      setState(s => ({
        ...s,
        eligibility_points1: awardManageresult[0]?.eligibility_points ?? 0,
        eligibility_points2: awardManageresult[1]?.eligibility_points ?? 0,
        eligibility_points3: awardManageresult[2]?.eligibility_points ?? 0,
        discount1: awardManageresult[0]?.discount ?? 0,
        discount2: awardManageresult[1]?.discount ?? 0,
        discount3: awardManageresult[2]?.discount ?? 0,
        gift1: awardManageresult[0]?.gift ?? null, // Set actual gift ID
        gift2: awardManageresult[1]?.gift ?? null, // Set actual gift ID
        gift3: awardManageresult[2]?.gift ?? null, // Set actual gift ID
        giftDisplay1: (() => {
          const giftId = awardManageresult[0]?.gift;
          if (!giftId) return null;
          const gift = getproductsresult.results
            .filter(item => item.is_gift)
            .find(p => p.id === giftId);
          return gift ? getLocalizedGiftName(gift) || gift.name : null;
        })(),
        giftDisplay2: (() => {
          const giftId = awardManageresult[1]?.gift;
          if (!giftId) return null;
          const gift = getproductsresult.results
            .filter(item => item.is_gift)
            .find(p => p.id === giftId);
          return gift ? getLocalizedGiftName(gift) || gift.name : null;
        })(),
        giftDisplay3: (() => {
          const giftId = awardManageresult[2]?.gift;
          if (!giftId) return null;
          const gift = getproductsresult.results
            .filter(item => item.is_gift)
            .find(p => p.id === giftId);
          return gift ? getLocalizedGiftName(gift) || gift.name : null;
        })(),
        bonusData: getbonusesresult,
        productsData: getproductsresult.results.filter(item => item.is_gift),
      }));

      setLoadingData(false);
      return true;
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingData(false);
      toast.error(t('pointsSetup.fetchError', 'Failed to fetch rewards data'));
      return false;
    }
  };

  const getStoreLevels = async () => {
    try {
      setLevelOptionDictLoading(true);

      const response = await doFetch('/admin-api/v1/get-store-levels');

      if (response && response.length > 0) {
        const levelsData = response;

        // Create the options list for the dropdown
        const optionsList = [
          {label: t('pointsSetup.selectLevel'), value: '-1'},
        ];
        if (Array.isArray(levelsData)) {
          levelsData.forEach(level => {
            if (level && level.id && level.level_display) {
              optionsList.push({
                label:
                  level.level_display ||
                  t('pointsSetup.unknownLevel', 'Unknown Level'),
                value: level.id.toString(),
              });
            }
          });
        }

        setLevelOptionsDict(optionsList);
      } else {
        // If no data, set default options
        setLevelOptionsDict([
          {label: t('pointsSetup.selectLevel'), value: '-1'},
        ]);
      }
    } catch (err) {
      console.error('Error fetching store levels:', err);
      // Set default options in case of error
      setLevelOptionsDict([{label: t('pointsSetup.selectLevel'), value: '-1'}]);
      toast.error(t('pointsSetup.errorLoadingLevels', 'Error loading levels'));
    } finally {
      setLevelOptionDictLoading(false);
    }
  };

  const DelBonuses = useMutation(params => {
    delRewardApi(params);
    toast.success(t('pointsSetup.rewardsRemoved'));
    fetchData();
  });

  const RemoveReward = async id => {
    await DelBonuses.mutateAsync(id);
    fetchData();
    setTimeout(() => {
      setRefresh(!refresh);
    }, 1200);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoadingData(true);
      try {
        await fetchData();
        await getStoreLevels();
        setLoadingData(false);
      } catch (error) {
        console.error('Error loading initial data:', error);
        toast.error(t('pointsSetup.loadingError', 'Error loading data'));
        setLoadingData(false);
      }
    };

    loadInitialData();
  }, [refresh]);

  // Separate handler for expanding/collapsing the awards row
  const toggleAwardRow = key => {
    if (selectedRow === key) {
      setSelectedRow(null);
    } else {
      setSelectedRow(key);
    }
  };

  const itemCollapser = key => {
    const isExpanded = selectedRow === key;
    return (
      <img
        onClick={() => toggleAwardRow(key)}
        src={isExpanded ? expandedImage : collapsedImage}
        alt={isExpanded ? 'Collapse' : 'Expand'}
        className="collapser-image"
        style={{cursor: 'pointer'}}
      />
    );
  };

  const GiftApiCall = async (awardId, productID) => {
    try {
      await doFetch(`/admin-api/v1/level-award/${awardId}/gift/${productID}`);
    } catch (err) {
      console.log('Error in GiftApiCall');
    }
  };

  const SubmitProductup = useMutation(params => PATCHUpdateRewardApi(params));

  const submit = async (id, data) => {
    try {
      // Toggle enabled status
      const enableStatus = !data.enabled;

      await SubmitProductup.mutateAsync({
        id: id,
        award_type_en: data.award_type_en || data.award_type_es_mx,
        discount: data.discount,
        points: data.points,
        level: data.level,
        enabled: enableStatus,
      });

      toast.success(t('pointsSetup.statusUpdated', 'Award status updated'));

      // Refresh data from server to get the updated state
      // Remove the immediate local state update to prevent ordering issues
      await fetchData();
    } catch (error) {
      console.error('Error updating award:', error);
      toast.error(t('pointsSetup.updateError', 'Failed to update award'));
    }
  };

  const Enabled = async (id, data) => {
    await submit(id, data);
  };

  return (
    <ContentWrapper
      breadcrumb={[
        {title: t('sidebar.menuItems.pointsSetup'), link: '/points-setup'},
      ]}
      onButtonOneClick={() => {
        for (let i = 0; i < awardData.length; i++) {
          awardSave(awardData[i]);
        }
      }}
      onButtonTwoClick={() => window.location.reload()}
      hasSearch={false}
      isEditedView={isEditedView}>
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
        <Card
          title={t('pointsSetup.bronze')}
          icon={<img src={Bronze} alt="Bronze Icon" />}
          rightContent={
            <PointsInput
              value={state.eligibility_points1}
              onChange={event => {
                var value = parseInt(event.target.value);
                var awards = awardData;
                awards[0].eligibility_points = value;
                setState(s => ({
                  ...s,
                  eligibility_points1: awardData[0]?.eligibility_points,
                  eligibility_points2: awardData[1]?.eligibility_points,
                  eligibility_points3: awardData[2]?.eligibility_points,
                  discount1: awardData[0]?.discount,
                  discount2: awardData[1]?.discount,
                  discount3: awardData[2]?.discount,
                  gift1: awardData[0].gift,
                  gift2: awardData[1].gift,
                  gift3: awardData[2].gift,
                }));
                setawardData(awards);
                setEditedView(true);
              }}
            />
          }>
          <div>
            <p className="gray-text">{t('pointsSetup.awardsForLevel')}</p>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              {/* <Switch enabled={state.discount1 ? true : false}/> */}
              {t('pointsSetup.discount')}
            </div>
            <div>
              <Input
                placeholder={'-'}
                value={state.discount1}
                onChange={event => {
                  var value = event.target.value;
                  var awards = awardData;
                  awards[0].discount = value;
                  setawardData(awards);

                  setState(s => ({
                    ...s,
                    eligibility_points1: awardData[0]?.eligibility_points,
                    eligibility_points2: awardData[1]?.eligibility_points,
                    eligibility_points3: awardData[2]?.eligibility_points,
                    discount1: awardData[0]?.discount,
                    discount2: awardData[1]?.discount,
                    discount3: awardData[2]?.discount,
                    gift1: awardData[0]?.gift,
                    gift2: awardData[1]?.gift,
                    gift3: awardData[2]?.gift,
                  }));
                  setEditedView(true);
                }}
                white
              />
            </div>
          </div>
          {/* <div style={{display:"flex",float:"left"}}><Switch enabled={awardData[0]?.gift ? true : false}  onChange={() => { 
            var awards = awardData; 
            awards[0].gift = !awardData[0]?.gift; 
            setawardData(awards);
            console.log(awardData);
            setEditedView(true);
          }}/>Gift</div><br/><br/>        */}

          {/* <div>
            <p>Item name lorem ipsum dolor</p>
          </div> */}
          {/* <div>
            <p className='tabbed-link'>Change</p>
          </div> */}
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <div style={{}}>
              <h4>{t('pointsSetup.gift')}: </h4>
            </div>
            <div>
              <TruncatedGiftName
                giftName={state.giftDisplay1}
                noGiftText={t('pointsSetup.noGift')}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <div>
              {!state.gift1 ? (
                <h4 title={t('pointsSetup.selectGift')}>{t('pointsSetup.selectGift')} </h4>
              ) : (
                <h4 title={t('pointsSetup.changeGift')}>{t('pointsSetup.changeGift')} </h4>
              )}
            </div>
            <div style={{width: '50%'}}>
              {!loadingData && (
                <Dropdown
                  items={state.productsData}
                  value={state.gift1}
                  placeholder={'-'}
                  callback={async item => {
                    var awards = awardData;
                    awards[0].gift = item;
                    setawardData(awards);

                    // Find the selected gift name to update the display
                    const selectedGift = state.productsData.find(
                      product => product.id === item,
                    );
                    const giftName = selectedGift
                      ? getLocalizedGiftName(selectedGift) || selectedGift.name
                      : null;

                    setState(s => ({
                      ...s,
                      gift1: item, // Keep the ID of the selected gift
                      giftDisplay1: giftName,
                    }));
                    setEditedView(true);

                    // Auto-save the changes to persist them
                    try {
                      await awardSave(awards[0]);
                    } catch (error) {
                      console.error('Failed to save gift selection:', error);
                    }
                  }}
                  white
                />
              )}
            </div>
          </div>

          {/* <Dropdown 
              items={state.productsData}  placeholder={"-"} callback={(item)=>{
              setState();
              }} white
            
          /> */}
        </Card>
        <Card
          title={t('pointsSetup.silver')}
          icon={<img src={Silver} alt="Silver Icon" />}
          rightContent={
            <PointsInput
              value={state.eligibility_points2}
              onChange={event => {
                var value = event.target.value;
                var awards = awardData;
                awards[1].eligibility_points = value;
                setState(s => ({
                  ...s,
                  eligibility_points1: awardData[0]?.eligibility_points,
                  eligibility_points2: awardData[1]?.eligibility_points,
                  eligibility_points3: awardData[2]?.eligibility_points,
                  discount1: awardData[0]?.discount,
                  discount2: awardData[1]?.discount,
                  discount3: awardData[2]?.discount,
                  gift1: awardData[0].gift,
                  gift2: awardData[1].gift,
                  gift3: awardData[2].gift,
                }));
                setawardData(awards);
                setEditedView(true);
              }}
            />
          }>
          <div>
            <p className="gray-text">{t('pointsSetup.awardsForLevel')}</p>
          </div>
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              {/* <Switch enabled={state.discount2 ? true : false}/> */}
              {t('pointsSetup.discount')}
            </div>
            <div>
              <Input
                placeholder={'-'}
                value={state.discount2}
                onChange={event => {
                  var value = event.target.value;
                  var awards = awardData;
                  awards[1].discount = value;
                  setawardData(awards);
                  setState(s => ({
                    ...s,
                    eligibility_points1: awardData[0]?.eligibility_points,
                    eligibility_points2: awardData[1]?.eligibility_points,
                    eligibility_points3: awardData[2]?.eligibility_points,
                    discount1: awardData[0]?.discount,
                    discount2: awardData[1]?.discount,
                    discount3: awardData[2]?.discount,
                  }));
                  setEditedView(true);
                }}
                white
              />
            </div>
          </div>
          {/* <div style={{display:"flex",float:"left"}}><Switch enabled={awardData[1]?.gift ? true : false} onChange={() => { 
            var awards = awardData; 
            awards[1].gift = !awardData[1]?.gift; 
            setawardData(awards);
            console.log(awardData);
            setEditedView(true);
          }}/>Gift</div><br/><br/>        */}
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <div style={{}}>
              <h4>{t('pointsSetup.gift')}: </h4>
            </div>
            <div>
              <TruncatedGiftName
                giftName={state.giftDisplay2}
                noGiftText={t('pointsSetup.noGift')}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <div>
              {!state.gift2 ? (
                <h4 title={t('pointsSetup.selectGift')}>{t('pointsSetup.selectGift')} </h4>
              ) : (
                <h4 title={t('pointsSetup.changeGift')}>{t('pointsSetup.changeGift')} </h4>
              )}
            </div>
            <div style={{width: '50%'}}>
              {!loadingData && (
                <Dropdown
                  items={state.productsData}
                  value={state.gift2}
                  placeholder={'-'}
                  callback={async item => {
                    var awards = awardData;
                    awards[1].gift = item;
                    setawardData(awards);

                    // Find the selected gift name to update the display
                    const selectedGift = state.productsData.find(
                      product => product.id === item,
                    );
                    const giftName = selectedGift
                      ? getLocalizedGiftName(selectedGift) || selectedGift.name
                      : null;

                    setState(s => ({
                      ...s,
                      gift2: item, // Keep the ID of the selected gift
                      giftDisplay2: giftName,
                    }));
                    setEditedView(true);

                    // Auto-save the changes to persist them
                    try {
                      await awardSave(awards[1]);
                    } catch (error) {
                      console.error('Failed to save gift selection:', error);
                    }
                  }}
                  white
                />
              )}
            </div>
          </div>

          {/* <div style={{textAlign:"center"}}> 
            <p className='gray-text'>--------- &nbsp;&nbsp; Gift is inactive  &nbsp;&nbsp; ---------</p>
          </div> */}
        </Card>
        <Card
          title={t('pointsSetup.gold')}
          icon={<img src={Gold} alt="Gold Icon" />}
          rightContent={
            <PointsInput
              value={state.eligibility_points3}
              // placeholder="099"
              onChange={event => {
                var value = event.target.value;
                var awards = awardData;
                awards[2].eligibility_points = value;
                setState(s => ({
                  ...s,
                  eligibility_points1: awardData[0]?.eligibility_points,
                  eligibility_points2: awardData[1]?.eligibility_points,
                  eligibility_points3: awardData[2]?.eligibility_points,
                  discount1: awardData[0]?.discount,
                  discount2: awardData[1]?.discount,
                  discount3: awardData[2]?.discount,
                }));
                setawardData(awards);
                setEditedView(true);
              }}
            />
          }>
          <div>
            <p className="gray-text">{t('pointsSetup.awardsForLevel')}</p>
          </div>
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              {/* <Switch enabled={state.discount3 ? true : false}/> */}
              {t('pointsSetup.discount')}
            </div>

            <div>
              <Input
                value={state.discount3}
                onChange={event => {
                  var value = event.target.value;
                  var awards = awardData;
                  awards[2].discount = value;
                  setawardData(awards);
                  setState(s => ({
                    ...s,
                    eligibility_points1: awardData[0]?.eligibility_points,
                    eligibility_points2: awardData[1]?.eligibility_points,
                    eligibility_points3: awardData[2]?.eligibility_points,
                    discount1: awardData[0]?.discount,
                    discount2: awardData[1]?.discount,
                    discount3: awardData[2]?.discount,
                  }));
                  setEditedView(true);
                }}
                white
              />
            </div>
          </div>
          {/* <div style={{display:"flex",float:"left"}}><Switch enabled={awardData[2]?.gift ? true : false} onChange={() => { 
            var awards = awardData; 
            awards[2].gift = !awardData[2]?.gift; 
            setawardData(awards);
            console.log(awardData);
            setEditedView(true);
          }}/>Gift</div><br/><br/>        */}
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <div style={{}}>
              <h4>{t('pointsSetup.gift')}: </h4>
            </div>
            <div>
              <TruncatedGiftName
                giftName={state.giftDisplay3}
                noGiftText={t('pointsSetup.noGift')}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <div>
              {!state.gift3 ? (
                <h4 title={t('pointsSetup.selectGift')}>{t('pointsSetup.selectGift')} </h4>
              ) : (
                <h4 title={t('pointsSetup.changeGift')}>{t('pointsSetup.changeGift')} </h4>
              )}
            </div>
            <div style={{width: '50%'}}>
              {!loadingData && (
                <Dropdown
                  items={state.productsData}
                  value={state.gift3}
                  placeholder={'-'}
                  callback={async item => {
                    var awards = awardData;
                    awards[2].gift = item;
                    setawardData(awards);

                    // Find the selected gift name to update the display
                    const selectedGift = state.productsData.find(
                      product => product.id === item,
                    );
                    const giftName = selectedGift
                      ? getLocalizedGiftName(selectedGift) || selectedGift.name
                      : null;

                    setState(s => ({
                      ...s,
                      gift3: item, // Keep the ID of the selected gift
                      giftDisplay3: giftName,
                    }));
                    setEditedView(true);

                    // Auto-save the changes to persist them
                    try {
                      await awardSave(awards[2]);
                    } catch (error) {
                      console.error('Failed to save gift selection:', error);
                    }
                  }}
                  white
                />
              )}
            </div>
          </div>

          {/* <div>
            <p>Item name lorem ipsum dolor</p>
          </div>
          <div>
            <p className='tabbed-link'>Change</p>
          </div> */}
        </Card>
      </div>
      <div style={{marginTop: '50px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between'}}>
          <div>
            <p className="font-bold mb-4">
              {t('pointsSetup.awardsManagement')}
            </p>
            <Button
              type="primary"
              label={t('pointsSetup.addReward')}
              onClick={() => openModal()}
            />
          </div>
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            style={customStyles}>
            <form>
              <h2>{t('pointsSetup.addRewardTitle')}</h2>
              <Input
                value={awardType}
                type="text"
                onChange={e => setAwardType(e.target.value)}
                label={t('pointsSetup.awardTypeEnglish')}
              />
              <Input
                value={awardTypeEs}
                type="text"
                onChange={e => setAwardTypeEs(e.target.value)}
                label={t('pointsSetup.awardTypeSpanish')}
              />
              <Input
                value={discount}
                type="number"
                min="0"
                max="100"
                step="0.01"
                onChange={handleDiscountChange}
                onKeyDown={handleKeyDown}
                label={t('pointsSetup.discount')}
              />
              <Input
                value={points}
                type="number"
                min="0"
                step="1"
                onChange={handlePointsChange}
                onKeyDown={handleKeyDown}
                label={t('pointsSetup.points')}
              />

              {!levelOptionsDictLoading ? (
                <>
                  <label className="text-black text-xs uppercase font-light">
                    {t('pointsSetup.level')}
                  </label>
                  <div className="py-1">
                    <select
                      className="w-full text-black uppercase font-light p-1"
                      value={selectedLevelOption}
                      onChange={e => setSelectedLevelOption(e.target.value)}
                      required>
                      {levelOptionsDict.map(option => (
                        <option
                          key={option.value || 'placeholder'}
                          value={option.value || ''}
                          className="">
                          {option.label || ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <div>{t('pointsSetup.loading')}</div>
              )}
              <div className="checkbox-row">
                <div>
                  <label className="checkbox-label">
                    {t('pointsSetup.enabled')}
                  </label>
                </div>
                <div>
                  <Input
                    type="checkbox"
                    checked={enabled}
                    onChange={handleCheckboxChange}
                  />
                </div>
              </div>
              <Button
                label={t('pointsSetup.save')}
                onClick={addAward}
                type="submit"
                disabled={
                  !awardType ||
                  !awardTypeEs ||
                  !selectedLevelOption ||
                  selectedLevelOption === '-1' ||
                  discount === '' ||
                  discount === null ||
                  discount === undefined ||
                  points === '' ||
                  points === null ||
                  points === undefined ||
                  validateDiscountAndPoints(discount, points) !== ''
                }
              />
            </form>
          </Modal>

          {/* <div style={{ display: 'flex' }}>
            <p style={{ marginRight: 10, marginTop: 5 }}>{t('pointsSetup.pointsExpire')}</p>
            <img
              src={questionMark}
              alt="?"
              style={{
                width: 15,
                height: 15,
                marginTop: 11,
                marginRight: 10,
                cursor: 'pointer',
              }}
              title="Help text here"
            />
            <Select options={pointexpire} />
          </div> */}
        </div>
        {awardData?.map((item, key) => (
          <div style={{marginTop: '20px'}} key={`award-${key}`}>
            <table style={{width: '100%', marginTop: '20px'}}>
              <thead
                className="bg-gray-100"
                style={{textAlign: 'center', height: '50px'}}>
                <tr>
                  <td style={{width: '45px'}}>{itemCollapser(key)}</td>
                  <td style={{width: '17%'}}>
                    {item.level && t(`pointsSetup.${item.level.toLowerCase()}`)}
                  </td>
                  <td style={{width: '20%'}}>{t('pointsSetup.status')}</td>
                  <td style={{width: '15%'}}>{t('pointsSetup.discount2')}</td>
                  <td style={{width: '15%'}}>{t('pointsSetup.points')}</td>
                  <td style={{width: '20%'}}>{t('pointsSetup.gift2')}</td>
                  <td style={{width: '20%'}}>{t('pointsSetup.action')}</td>
                </tr>
              </thead>
              {selectedRow === key && (
                <tbody className="content">
                  {item?.awards && item.awards.length > 0 ? (
                    item.awards.map((itemaward, awardIndex) => (
                      <tr
                        className="text-center"
                        key={`award-item-${itemaward.id}-${awardIndex}`}>
                        <td style={{width: '45px'}} />
                        <td style={{textAlign: 'left', width: '17%'}}>
                          {getLocalizedAwardType(itemaward) ||
                            itemaward.award_type_display ||
                            t(
                              'pointsSetup.noAwardType',
                              'No award type available',
                            )}
                        </td>
                        <td style={{textAlign: 'center', width: '20%'}}>
                          <Switch
                            onChange={() => Enabled(itemaward.id, itemaward)}
                            enabled={itemaward && itemaward.enabled}
                          />
                        </td>
                        <td
                          style={{
                            textAlign: 'center',
                            width: '20%',
                            paddingLeft: 25,
                            paddingRight: 25,
                          }}>
                          <Input
                            value={itemaward.discount ?? ''}
                            placeholder={'-'}
                            white
                            readOnly
                          />
                        </td>
                        <td
                          style={{
                            textAlign: 'center',
                            width: '20%',
                            paddingLeft: 25,
                            paddingRight: 25,
                          }}>
                          <Input
                            value={itemaward.points ?? ''}
                            placeholder={'-'}
                            white
                            readOnly
                          />
                        </td>
                        <td
                          style={{
                            textAlign: 'center',
                            width: '20%',
                            paddingLeft: 25,
                            paddingRight: 25,
                          }}>
                          <Dropdown
                            items={state.productsData}
                            value={itemaward?.gift_display?.id}
                            placeholder={'-'}
                            white
                            callback={item => {
                              GiftApiCall(itemaward.id, item);
                              let gift_name = state.productsData.filter(
                                e => e.id === item,
                              );

                              if (gift_name.length > 0) {
                                let tmpData = [...awardData];
                                tmpData.forEach(e => {
                                  if (e.awards && e.awards.length > 0) {
                                    e.awards.forEach(i => {
                                      if (i.id === itemaward.id) {
                                        i.gift_display = {
                                          name: gift_name[0].name,
                                          id: gift_name[0].id,
                                        };
                                      }
                                    });
                                  }
                                });

                                setawardData(tmpData);
                                setEditedView(true);
                              }
                            }}
                          />
                        </td>
                        <td style={{textAlign: 'left', width: '17%'}}>
                          <div className="flex items-center">
                            <div className="px-4">
                              <p className="text-left">
                                <button
                                  className="tabbed-link"
                                  style={{marginLeft: -15}}
                                  onClick={() => RemoveReward(itemaward.id)}>
                                  {t('pointsSetup.remove')}
                                </button>
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        {t(
                          'pointsSetup.noRewards',
                          'No rewards found for this level',
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
          </div>
        ))}

        {/* <div style={{marginTop: '20px'}}>
          {t('pointsSetup.extraBonus')}
          <table style={{width: '100%', marginTop: '20px'}}>
            <thead
              className="bg-gray-100"
              style={{textAlign: 'center', height: '50px'}}>
              <td>{t('pointsSetup.amount')}</td>
              <td>{t('pointsSetup.status')}</td>
              <td>{t('pointsSetup.discount')}</td>
              <td>{t('pointsSetup.points')}</td>
              <td>{t('pointsSetup.gift')}</td>
              <td>{t('pointsSetup.action')}</td>
            </thead>

            <tbody className="content">
              {state.bonusData?.map((item, key) => (
                <tr
                  style={{marginTop: 0, marginBottom: 0}}
                  key={key}
                  className="text-center">
                  <td
                    style={{
                      textAlign: 'center',
                      width: '20%',
                      paddingLeft: 25,
                      paddingRight: 25,
                      marginTop: 0,
                      marginBottom: 0,
                    }}>
                    <Input value={item.amount | 0} white placeholder={'-'} />
                  </td>
                  <td>
                    <Switch
                      enabled={item.enabled}
                      onChange={() => (item.enabled = !item.enabled)}
                    />
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      width: '20%',
                      paddingLeft: 25,
                      paddingRight: 25,
                    }}>
                    <Input value={item.discount} white placeholder={'-'} />
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      width: '20%',
                      paddingLeft: 25,
                      paddingRight: 25,
                    }}>
                    <Input value={item.points} white placeholder={'-'} />
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      width: '20%',
                      paddingLeft: 25,
                      paddingRight: 25,
                    }}>
                    <Dropdown
                      items={state.productsData}
                      value={item.gift_display?.name}
                      placeholder={'-'}
                      callback={item => {
                        console.log('item >>>>> +++ >>', item);
                      }}
                      white
                    />
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      width: '20%',
                      paddingLeft: 25,
                      paddingRight: 25,
                    }}>
                    <p
                      className="tabbed-link"
                      onClick={() => RemoveBonus(item.id)}>
                      Remove
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{marginTop: 15}}>
            <p className="tabbed-link" onClick={() => addMore()}>
              {t('pointsSetup.addMore')}
            </p>
          </div>
        </div> */}
      </div>
    </ContentWrapper>
  );
};

export default PointsSetup;
